import { useMemo } from "react";
import { Slider } from "@/components/ui/slider";
import { Fuel } from "lucide-react";

/**
 * Digital fuel-gauge input — mimics a real dashboard fuel gauge with 10 LED
 * bars from E (red, empty) to F (full). User drags a Radix slider underneath
 * to pick a value in 5% increments; the LED bars visualise the current level.
 *
 * Value range: 0–100 (integer, snapped to nearest 5). Empty string clears.
 * Emits raw numeric values via `onChange(String(pct))` for API-uniformity
 * with the existing form-state string convention.
 */
export default function FuelGaugeInput({
  value,
  onChange,
  ariaLabel,
  testIdPrefix = "fuel-gauge",
}) {
  const numeric = value === "" || value == null ? null : Number(value);
  const sliderVal = numeric == null ? 0 : Math.max(0, Math.min(100, numeric));

  // 10 LED segments, filled based on numeric value. Last-lit segment glows
  // a touch brighter; empty segments render at faint outline only. Segment
  // 0 (leftmost) is the E indicator — turns red when the gauge is <=15%.
  const bars = useMemo(() => {
    const segs = new Array(10).fill(0);
    if (numeric == null) return segs;
    // 0-9 → each segment covers 10 percentage points; the leftmost lights
    // up at anything > 0, the rightmost only at 100.
    const litCount = Math.max(0, Math.min(10, Math.round(numeric / 10)));
    for (let i = 0; i < litCount; i += 1) segs[i] = 1;
    return segs;
  }, [numeric]);

  const showLowFuel = numeric != null && numeric <= 15;

  const handleSlider = ([v]) => {
    const snapped = Math.round(v / 5) * 5; // 5% steps
    onChange(String(snapped));
  };

  // Quick presets — tap to jump to a common fraction without dragging.
  const presets = [
    { v: 0, label: "E" },
    { v: 25, label: "¼" },
    { v: 50, label: "½" },
    { v: 75, label: "¾" },
    { v: 100, label: "F" },
  ];

  return (
    <div className="space-y-2" data-testid={testIdPrefix}>
      {/* LED bar display */}
      <div
        className="flex items-center gap-2 px-2 py-2 bg-[var(--bg-canvas)] border border-[var(--border-subtle)]"
        style={{ borderRadius: 2 }}
      >
        <Fuel
          className={`w-3.5 h-3.5 shrink-0 ${
            showLowFuel ? "text-[#E82127]" : "text-[var(--text-muted)]"
          }`}
          strokeWidth={1.75}
        />
        <span
          className={`text-[10px] font-mono-data tracking-wider w-3 text-center ${
            showLowFuel ? "text-[#E82127]" : "text-[var(--text-muted)]"
          }`}
        >
          E
        </span>
        <div className="flex-1 flex items-center gap-[3px]" aria-hidden="true">
          {bars.map((lit, i) => (
            <div
              key={i}
              className="flex-1 transition-colors"
              style={{
                height: 14,
                borderRadius: 1,
                backgroundColor: lit
                  ? i === 0 && showLowFuel
                    ? "#E82127"
                    : "var(--text-primary)"
                  : "var(--border-subtle)",
                opacity: lit ? 1 : 0.55,
              }}
            />
          ))}
        </div>
        <span className="text-[10px] font-mono-data tracking-wider w-3 text-center text-[var(--text-muted)]">
          F
        </span>
        <span
          className="text-[10px] font-mono-data tracking-wider w-9 text-right text-[var(--text-primary)]"
          data-testid={`${testIdPrefix}-value`}
        >
          {numeric == null ? "—" : `${Math.round(numeric)}%`}
        </span>
      </div>

      {/* Slider + presets */}
      <div className="space-y-1.5">
        <Slider
          value={[sliderVal]}
          onValueChange={handleSlider}
          min={0}
          max={100}
          step={5}
          aria-label={ariaLabel}
          data-testid={`${testIdPrefix}-slider`}
        />
        <div
          className="flex items-center justify-between"
          data-testid={`${testIdPrefix}-presets`}
        >
          {presets.map((p) => {
            const active = numeric === p.v;
            return (
              <button
                key={p.v}
                type="button"
                onClick={() => onChange(String(p.v))}
                data-testid={`${testIdPrefix}-preset-${p.v}`}
                className={`tap px-2 py-0.5 text-[10px] font-mono-data tracking-wider transition-colors ${
                  active
                    ? "text-[var(--text-primary)]"
                    : "text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                }`}
                style={{ borderRadius: 1 }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
